import { TRoomDetail } from '@/entities/room'
import { cn, formatCurrency } from '@/shared/lib'
import { ContentBox, Notice, Separator } from '@/shared/ui'

type Props = Pick<
  TRoomDetail,
  'longTermDiscounts' | 'earlyCheckinDiscounts'
> & {
  className?: string
  highlightColor?: 'purple' | 'gray'
}
export const Discounts = ({
  longTermDiscounts,
  earlyCheckinDiscounts,
  highlightColor = 'purple',
  className,
}: Props) => {
  const hasLongTermDiscount = longTermDiscounts.length > 0
  const hasEarlyCheckinDiscount = earlyCheckinDiscounts.length > 0

  const hasAllDiscount = hasLongTermDiscount && hasEarlyCheckinDiscount
  const hasNoDiscount = !hasLongTermDiscount && !hasEarlyCheckinDiscount

  if (hasNoDiscount) {
    return null
  }

  return (
    <ContentBox
      color="light-gray"
      spacing="medium"
      className={cn('space-y-4', className)}
    >
      {hasLongTermDiscount && (
        <LongTermDiscount
          longTermDiscounts={longTermDiscounts}
          highlightColor={highlightColor}
        />
      )}

      {hasAllDiscount && <Separator color="light-gray" />}

      {hasEarlyCheckinDiscount && (
        <EarlyCheckinDiscount
          earlyCheckinDiscounts={earlyCheckinDiscounts}
          highlightColor={highlightColor}
        />
      )}
    </ContentBox>
  )
}

const LongTermDiscount = ({
  longTermDiscounts,
  highlightColor,
}: Pick<TRoomDetail, 'longTermDiscounts'> & Pick<Props, 'highlightColor'>) => {
  return (
    <div className="space-y-2">
      <h4 className="typo-label1 font-semibold text-black">장기 계약 할인</h4>
      <Notice.List gap={false} className="space-y-1">
        {longTermDiscounts.map(
          ({ weeks, discountRate, discountedUsingFee }) => (
            <Notice.Item
              key={weeks}
              className="flex items-center justify-between"
            >
              <div className="typo-label2 font-normal text-gray-900">
                <span
                  className={cn(
                    'font-semibold',
                    highlightColor === 'purple' && 'text-purple-500',
                  )}
                >
                  {weeks}주
                </span>{' '}
                이상 계약 시 {discountRate}% 할인
              </div>
              <div className="typo-label2 flex items-center gap-1 font-normal">
                <span className="font-semibold text-black">
                  {formatCurrency(discountedUsingFee)}원
                </span>
                <span className="typo-caption1 text-gray-200">/</span>
                <span className="text-gray-900">1주</span>
              </div>
            </Notice.Item>
          ),
        )}
      </Notice.List>
    </div>
  )
}

const EarlyCheckinDiscount = ({
  earlyCheckinDiscounts,
  highlightColor,
}: Pick<TRoomDetail, 'earlyCheckinDiscounts'> &
  Pick<Props, 'highlightColor'>) => {
  return (
    <div className="space-y-2">
      <h4 className="typo-label1 font-semibold text-black">즉시 입주 할인</h4>
      <Notice.List gap={false} className="space-y-1">
        {earlyCheckinDiscounts.map(({ days, discountAmount }) => {
          const highlightLabel = days === 0 ? '오늘' : `오늘부터 ${days}일 이내`

          return (
            <Notice.Item
              key={days}
              className="flex items-center justify-between"
            >
              <div className="typo-label2 font-normal text-gray-900">
                <span
                  className={cn(
                    'font-semibold',
                    highlightColor === 'purple' && 'text-purple-500',
                  )}
                >
                  {highlightLabel}
                </span>{' '}
                입주 시
              </div>
              <span className="typo-label2 font-semibold text-black">
                {discountAmount}만 원 할인
              </span>
            </Notice.Item>
          )
        })}
      </Notice.List>
    </div>
  )
}
